import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { authApi } from "@/lib/apiClient";
import { AUTH } from "@/constants/testIds";

export default function PasswordResetRequest() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [devToken, setDevToken] = useState("");
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await authApi.requestReset(email);
      setDone(true);
      if (res.dev_token) setDevToken(res.dev_token);
    } catch (err) {
      const code = err.response?.status;
      setError(
        code === 429
          ? "Too many requests. Please try again in a bit."
          : "Unable to submit request.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        <Card className="border shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl">Reset your password</CardTitle>
            <CardDescription>
              We&rsquo;ll send a reset link if this address matches an active account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {done ? (
              <div className="space-y-4">
                <Alert>
                  <AlertDescription>
                    If <span className="font-medium">{email}</span> matches an account, a reset link is on its way.
                  </AlertDescription>
                </Alert>
                {devToken && (
                  <Alert>
                    <AlertDescription className="break-all">
                      <span className="font-medium">Dev-only token:</span>{" "}
                      <Link
                        className="underline"
                        to={`/admin/password-reset/confirm?token=${encodeURIComponent(devToken)}`}
                      >
                        {devToken}
                      </Link>
                    </AlertDescription>
                  </Alert>
                )}
                <Link
                  to="/admin/login"
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  &larr; Back to sign in
                </Link>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="space-y-4"
                data-testid={AUTH.resetRequestForm}
                noValidate
              >
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid={AUTH.resetRequestEmail}
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={busy}
                  data-testid={AUTH.resetRequestSubmit}
                >
                  {busy ? "Sending..." : "Send reset link"}
                </Button>
                <Link
                  to="/admin/login"
                  className="text-sm text-muted-foreground hover:text-foreground block text-center"
                >
                  &larr; Back to sign in
                </Link>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
